import Link from "next/link";
import { useRouter } from "next/router";

import { useAuth } from "@/auth/hooks";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { openHomeFetch, useOpenHomeCopy } from "@/features/open-home/client";
import { OPEN_HOME_LOCALES } from "@/features/open-home/copy";
import {
  OPEN_HOME_GATE,
  OPEN_HOME_SEARCH_TAB_GATE,
  useOpenHomeGate,
} from "@/features/open-home/gate";
import { useMounted } from "@/features/open-home/utils/use-mounted";
import { useQuery } from "@tanstack/react-query";

/**
 * The scope toggle at the top of the main search (plan slide 5, E): "For sale" | "Open Home".
 * Members jump to the same query inside homes; visitors see how many pieces in homes match and a
 * sign-up prompt, which is the accelerator. Only a count ever reaches a logged-out browser.
 */
/** Rendered only when both the `open_home` and the `open_home_search_tab` Statsig gates are on. */
export const OpenHomeSearchTab = () => {
  const openHome = useOpenHomeGate(OPEN_HOME_GATE);
  const searchTab = useOpenHomeGate(OPEN_HOME_SEARCH_TAB_GATE);

  return openHome && searchTab ? <OpenHomeSearchTabContent /> : null;
};

const OpenHomeSearchTabContent = () => {
  const { locale, query } = useRouter();
  const copy = useOpenHomeCopy();
  const { isLoggedIn: hasToken } = useAuth();
  // Cookie-only state: identical server and first client render (no hydration mismatch).
  const isLoggedIn = useMounted() && hasToken;
  const { guestGuard } = useGuestModalGuard();
  const text = typeof query.query === "string" ? query.query : "";
  const enabled = OPEN_HOME_LOCALES.includes(locale as "en" | "nl");
  const count = useQuery({
    queryKey: ["open-home", "count", text],
    enabled: enabled && Boolean(text),
    staleTime: 60_000,
    queryFn: () =>
      openHomeFetch<{ count: number }>(
        null,
        `count?q=${encodeURIComponent(text)}`
      ),
  });

  if (!enabled) {
    return null;
  }

  const badge = count.data?.count ? (
    <span className="ml-1.5 rounded-full bg-attention-500 px-1.5 text-xs text-white">
      {count.data.count}
    </span>
  ) : null;

  return (
    <div
      className="flex w-fit rounded-full bg-brand-50 p-1 text-sm font-semibold"
      role="tablist"
    >
      <span
        role="tab"
        aria-selected
        className="rounded-full bg-white px-4 py-1.5 text-brand-700 shadow"
      >
        {copy.searchTabForSale}
      </span>
      {isLoggedIn ? (
        <Link
          role="tab"
          aria-selected={false}
          href="/open-home"
          className="rounded-full px-4 py-1.5 text-brand-600 hover:text-brand-800"
        >
          {copy.name}
          {badge}
        </Link>
      ) : (
        <button
          type="button"
          role="tab"
          aria-selected={false}
          onClick={() =>
            guestGuard({
              title: copy.loginTitle,
              description: copy.loginBody,
              referrer: "open-home-search-tab",
            })
          }
          className="rounded-full px-4 py-1.5 text-brand-600 hover:text-brand-800"
        >
          🔒 {copy.name}
          {badge}
        </button>
      )}
    </div>
  );
};
